import {
  fetchCopiedStudy,
  handleCreateStudy,
  studyNavigationState,
  STUDY_TYPES,
  FOLLOW_ON_STUDY_TYPE,
  hasCardioOneFacilities,
} from '@/entities/study';
import { useSupportStore } from '@/entities/support';
import { useLoadingStore } from '@/shared/model';
import { DEVICE, LOADING_TYPES } from '@/shared/config';
import {
  convertDecimalHeightToFeetInch,
  isWebViewCreateStudy,
  showMessageError,
  handleFetchCountries,
} from '@/shared/lib';
import { useMergeState } from '@/shared/lib/customHooks';
import _ from 'lodash';
import queryString from 'query-string';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { t } from 'ttag';
import { BIODIRECT_STEP_TITLES, STEP_TITLES, STUDY_CATEGORY } from '../../config/newStudy';
import { newStudyDataFormatter } from '../../lib/newStudy';

/**
 * Backs the create-study wizard with its step index, accumulated per-step payload, step titles and modal flags. Prefills the steps on mount from a copied study or the URL, advances or rewinds the wizard while merging step data, creates the study with the Bioflux Direct, ICOR and pending-assignment redirects, and scrolls the body back to the top on every step change.
 *
 * @returns The wizard state, refs and the navigation, modal and loading-flag handlers.
 */
const useNewStudy = () => {
  const { studyId } = useParams();

  const location = useLocation();

  const navigate = useNavigate();

  const supportFacilities = useSupportStore((s) => s.supportFacilities);

  /**
   * The full-page loading text setter pulled out of the loading store. Selected from useLoadingStore so calling it with a loading type shows the overlay and calling it with no argument clears it.
   *
   * @returns The store action that sets or clears the page loading text.
   */
  const loadingPageAction = useLoadingStore((s) => s.setLoadingText);

  const [state, setState] = useMergeState({
    step: 1,
    countries: [],
    isOpenConfirmStopProcessModal: false,
    stepTitlesCurrent: STEP_TITLES,
    isOpenDisclaimerModal: false,
    isLoadedData: false,
    isLoading: false,
    isLoadingDivisions: false,
    isDisabledNext: false,
    isLoadingDevice: false,
    isChangingFacility: false,
  });

  const newStudyData = useRef<any>({
    step1: {},
    step2: {},
    step3: {},
    step4: {},
    step5: {},
    studySettingStep: {},
    carePlan: {},
  });
  const isUsingStudyBiotresList = useRef(false);
  const newStudyStepRef = useRef<any>(null);

  // For responsive features: Case webview and resize events
  const isWebView = isWebViewCreateStudy();

  /**
   * Submits the assembled create-study payload and routes the user according to the result. Shows the create-study loading overlay, stores the returned study in the step 5 data and resets the studies list to its first tab, then leaves the wizard for Bioflux Direct, pending device assignment or ICOR devices and otherwise advances to step 5, logging and toasting any failure.
   *
   * @param input - Formatted study creation payload built from the wizard steps.
   * @returns Resolves after the study is created and the redirect or step advance has been applied.
   */
  const handleCreateNewStudy = async (input: any) => {
    try {
      loadingPageAction(LOADING_TYPES.CREATE_STUDY);
      const { study } = await handleCreateStudy(input);
      _.assign(newStudyData.current.step5, {
        study,
      });
      _.assign(studyNavigationState, { tab: '1' });
      if (
        input.studyCategory === STUDY_CATEGORY.BIOFLUX_DIRECT ||
        study?.isDeviceAssignmentPending ||
        study?.device?.model === DEVICE.ICOR
      ) {
        if (isWebView) {
          window.location.href = 'biotres://end-createstudy/success';
        } else if (study?.isDeviceAssignmentPending) {
          navigate('/studies', {
            state: {
              shouldNavigateToUnassignedTab: true,
            },
          });
        } else {
          navigate('/studies');
        }
      } else {
        setState({ step: state.step + 1 });
      }
    } catch (error) {
      console.error('Failed to create new study: ', error);
      showMessageError({ content: error instanceof Error ? error.message : '' });
    } finally {
      loadingPageAction();
    }
  };

  /**
   * Moves between the create-study wizard steps and merges each step's data into the accumulated payload. Steps up to 3 simply advance, with step 3 also copying the study settings and recording whether a Biotres device is used; step 4 either opens the patient-app disclaimer modal or formats the payload and creates the study, while a backward click decrements the step.
   *
   * @param stepData - Data submitted by the step being left, merged into the accumulated study payload.
   * @param isNextClicked - True to move forward, false to move back.
   * @returns Nothing.
   */
  const handleClick = (stepData: any, isNextClicked: any) => {
    if (!_.isEmpty(stepData)) {
      _.merge(newStudyData.current, stepData);
    }

    if (isNextClicked) {
      if (state.step <= 3) {
        if (state.step === 3) {
          isUsingStudyBiotresList.current = stepData?.step3?.model.includes(DEVICE.BIOTRES);

          _.assign(newStudyData.current.studySettingStep, {
            preEventTime: stepData?.step3?.preEventTime,
            postEventTime: stepData?.step3?.postEventTime,
            bradycardiaThreshold: stepData?.step3?.bradycardiaThreshold,
            tachycardiaThreshold: stepData?.step3?.tachycardiaThreshold,
            pauseLevel: stepData?.step3?.pauseLevel,
            afibDetection: stepData?.step3?.afibDetection,
            pauseDetection: stepData?.step3?.pauseDetection,
            diagnosticLead: stepData?.step3?.diagnosticLead,
            algAmp: stepData?.step3?.algAmp,
          });
        }

        setState({ step: state.step + 1 });
      } else if (state.step === 4) {
        const {
          deviceId,
          model,
          isDeviceAssignmentPending,
          isBiofluxDirect,
          studyType,
          facility,
          isTest,
        } = newStudyData.current.step3;
        const shouldOpenAppModal =
          deviceId &&
          model === DEVICE.BIOTRES &&
          !isDeviceAssignmentPending &&
          !isBiofluxDirect &&
          ![STUDY_TYPES.HOLTER, STUDY_TYPES.EXTENDED_HOLTER].includes(studyType) &&
          !(facility.status === 'Test' ? isTest : undefined);
        if (shouldOpenAppModal) {
          setState({ isOpenDisclaimerModal: true });
        } else {
          const formattedNewStudyData = newStudyDataFormatter(newStudyData.current);
          handleCreateNewStudy(formattedNewStudyData);
        }
      } else if (isWebView) {
        window.location.href = 'biotres://end-createstudy/success';
      } else {
        navigate('/studies');
      }
    } else {
      setState({ step: state.step - 1 });
    }
  };

  /**
   * Fetches the study being copied so the create-study wizard can prefill itself from it. Delegates to fetchCopiedStudy and, on failure, logs the error, surfaces it through showMessageError and returns null.
   *
   * @param studyFid - Identifier of the study to copy.
   * @returns The copied study record, or null when the fetch failed.
   */
  const handleFetchCopiedStudy = async (studyFid: any) => {
    try {
      return await fetchCopiedStudy(studyFid);
    } catch (error) {
      console.error('Failed to fetch copied study: ', error);
      showMessageError({ content: error instanceof Error ? error.message : '' });
      return null;
    }
  };

  /**
   * Bootstraps the create-study wizard, prefilling every step from a copied study or from a study passed in through the URL query. Fetches the countries alongside the copied study, maps onboarding, patient, contact, physician, study setting, diagnosis and insurance data onto step1 through step4, converting decimal height to feet and inches and deriving the amplification rate and duration units.
   *
   * @returns Resolves once the countries and all prefilled step data have been applied.
   */
  const createInitialData = async () => {
    const promises: Promise<any>[] = [handleFetchCountries()];

    let studyTmp;

    if (studyId) {
      loadingPageAction(LOADING_TYPES.COPYING_STUDY);
      promises.push(handleFetchCopiedStudy(studyId));
    } else if (location.search) {
      const parsed = queryString.parse(location.search);
      studyTmp = JSON.parse(decodeURIComponent(parsed.study as string));
    }

    const results = await Promise.all(promises);
    const countries = results[0];
    const study = studyId ? results[1] : studyTmp;
    if (study) {
      const onboarding = study?.onboarding || {};
      const { patientSymptoms, frequentlyOfSymptoms, usedHeartMonitor, pacer, icd } = onboarding;

      const info = study?.info || {};
      const facility = study?.facility || {};

      const studySetting = study?.info?.studySetting || {};
      const {
        duration,
        preEventTime,
        postEventTime,
        bradycardiaThreshold,
        tachycardiaThreshold,
        pauseLevel,
        pauseDetection,
        afibDetection,
        diagnosticLead,
      } = studySetting;

      const algAmp = _.get(
        facility.amplifyEcgSignal,
        `${_.camelCase(study.deviceType || DEVICE.BIOFLUX)}DeviceRate`,
      );

      const insurance = info?.insurance || {};
      const interprettingPhysician = info?.interprettingPhysician || {};
      const patient = info?.patient || {};

      const diagnosis = info?.diagnosis || {};
      const { physiciansNote, primary, second, patientMedications } = diagnosis;

      const referringPhysician = info?.referringPhysician || {};

      const {
        patientId,
        firstName,
        middleName,
        lastName,
        dob,
        gender,
        email,
        height,
        weight,
        ethnicity,
      } = patient;
      const contact = patient?.contact || {};
      const secondaryContact = patient?.secondaryContact || {};
      const { address, city, state, country, zip, phone1, phone2 } = contact;
      const heightFormat = convertDecimalHeightToFeetInch(height);

      _.assign(newStudyData.current.carePlan, {
        carePlanId: study.carePlanId,
        carePlanType: study.carePlanType,
      });

      const isHolterStudyReport = [STUDY_TYPES.HOLTER, FOLLOW_ON_STUDY_TYPE].includes(
        study.studyType,
      );

      _.assign(newStudyData.current, {
        step1: {
          patientId,
          firstName,
          middleName,
          lastName,
          dob,
          gender: gender || 'Male',
          pacer,
          icd,
          weight,
          heightFt: heightFormat?.ft,
          heightIn: heightFormat?.ins,
          ethnicity,
        },
        step2: {
          address,
          city,
          state,
          country,
          zip,
          phone1,
          email: email || undefined,
          phone2: phone2 || undefined,
          secondaryName: secondaryContact.name || undefined,
          secondaryRelationship: secondaryContact.relationship || undefined,
          secondaryPhone: secondaryContact.phone || undefined,
          secondaryEmail: secondaryContact.email || undefined,
        },
        step3: {
          facility,
          deviceId: '',
          referringPhysicianFirstName: referringPhysician.firstName,
          referringPhysicianLastName: referringPhysician.lastName,
          interprettingPhysicianFirstName: interprettingPhysician.firstName,
          interprettingPhysicianLastName: interprettingPhysician.lastName,
          referringPhysician: referringPhysician.id,
          interprettingPhysician: interprettingPhysician.id,
          studyType: study.studyType,
          followOnStudyType: study.followOnStudy?.studyType,
          studySettingDuration: duration,
          followOnStudyDuration: study.followOnStudy?.initDuration,
          preEventTime,
          postEventTime,
          bradycardiaThreshold,
          tachycardiaThreshold,
          pauseLevel,
          afibDetection: afibDetection || true,
          pauseDetection: pauseDetection || true,
          diagnosticLead: diagnosticLead || 2,
          clinicianInitials: info?.clinicianInitials,
          isTest: study.isTest,
          auto: false, // ?: Need get default value from copied data?
          model: study.deviceType || DEVICE.BIOFLUX,
          algAmp,
          unitTime: isHolterStudyReport ? (duration % 1440 === 0 ? 'day' : 'hour') : 'day',
          unitTimeFO: isHolterStudyReport
            ? (study.followOnStudy?.initDuration || 0) % 1440 === 0
              ? 'day'
              : 'hour'
            : 'day',
          preventUpdatingReportDueDate: false,
          isShouldShowStudyDurationAlert: true,
          isShouldShowFOStudyDurationAlert: true,
        },
        step4: {
          primary: !_.isNil(primary) && primary !== 'Other' ? primary : undefined,
          second: !_.isNil(second) && second !== 'Other' ? second : undefined,
          insuranceType: insurance.type,
          insuranceProvider: insurance.provider,
          insurancePreAuth: insurance.preAuth || 'true',
          patientMedications,
          physiciansNote,
          patientSymptoms,
          frequentlyOfSymptoms: frequentlyOfSymptoms?.[0],
          usedHeartMonitor,
        },
        step5: {},
      });

      loadingPageAction();
    }

    setState({ countries });

    if (!studyId) {
      navigate('/studies/create', { replace: true });
    }
  };

  /**
   * Handles the back action of the create-study wizard header. Opens the stop-process confirmation modal while still before step 5, otherwise signals failure through the biotres end-createstudy URL in the web view or returns to the studies list.
   *
   * @returns Nothing.
   */
  const handleClickBack = () => {
    if (state.step < 5) {
      setState({ isOpenConfirmStopProcessModal: true });
    } else if (isWebView) {
      window.location.href = 'biotres://end-createstudy/fail';
    } else {
      navigate('/studies');
    }
  };

  /**
   * Abandons the create-study wizard once the user confirms. Redirects to the biotres end-createstudy failure URL inside the native web view, otherwise navigates to the studies list.
   *
   * @returns Nothing.
   */
  const onClickYesButton = () => {
    if (isWebView) {
      window.location.href = 'biotres://end-createstudy/fail';
    } else {
      navigate('/studies');
    }
  };

  /**
   * Keeps the user inside the create-study wizard after they decline to abandon it. Closes the stop-process confirmation modal.
   *
   * @returns Nothing.
   */
  const onClickNoButton = () => {
    setState({ isOpenConfirmStopProcessModal: false });
  };

  /**
   * Creates the study once the user accepts the patient-app disclaimer. Closes the modal, formats the accumulated wizard data with newStudyDataFormatter and submits it through handleCreateNewStudy.
   *
   * @returns Nothing.
   */
  const onClickAgreeDisclaimerModal = () => {
    setState({ isOpenDisclaimerModal: false });
    const formattedNewStudyData = newStudyDataFormatter(newStudyData.current);
    handleCreateNewStudy(formattedNewStudyData);
  };

  /**
   * Dismisses the patient-app disclaimer of the create-study wizard without creating the study. Sets isOpenDisclaimerModal to false.
   *
   * @returns Nothing.
   */
  const onClickCancelDisclaimerModal = () => {
    setState({ isOpenDisclaimerModal: false });
  };

  /**
   * Switches the wizard's step titles to match the Bioflux Direct flow. Stores BIODIRECT_STEP_TITLES when the flag is set and the standard STEP_TITLES otherwise.
   *
   * @param flag - True when the study being created is a Bioflux Direct study.
   * @returns Nothing.
   */
  const updateStepTitleBiofluxDirect = (flag: any) => {
    if (flag) {
      setState({ stepTitlesCurrent: BIODIRECT_STEP_TITLES });
    } else {
      setState({ stepTitlesCurrent: STEP_TITLES });
    }
  };

  /**
   * Records whether the current wizard step has finished preparing its data. Merges the flag into the hook's state so the step can swap its skeleton for the real form.
   *
   * @param isLoadedData - True once the step's data is ready.
   * @returns Nothing.
   */
  const setIsLoadedData = (isLoadedData: any) => {
    setState({ isLoadedData });
  };

  /**
   * Records the create-study wizard's general loading state. Merges the flag into the hook's state.
   *
   * @param isLoading - True while the wizard is busy.
   * @returns Nothing.
   */
  const setIsLoading = (isLoading: any) => {
    setState({ isLoading });
  };

  /**
   * Records whether facility divisions are being loaded for the create-study wizard. Merges the flag into the hook's state.
   *
   * @param isLoadingDivisions - True while divisions are being fetched.
   * @returns Nothing.
   */
  const setIsLoadingDivisions = (isLoadingDivisions: any) => {
    setState({ isLoadingDivisions });
  };

  /**
   * Controls whether the create-study wizard's next button is disabled. Merges the flag into the hook's state.
   *
   * @param isDisabledNext - True to disable forward navigation.
   * @returns Nothing.
   */
  const setIsDisabledNext = (isDisabledNext: any) => {
    setState({ isDisabledNext });
  };

  /**
   * Records whether the device list is being loaded for the create-study wizard. Merges the flag into the hook's state.
   *
   * @param isLoadingDevice - True while devices are being fetched.
   * @returns Nothing.
   */
  const setIsLoadingDevice = (isLoadingDevice: any) => {
    setState({ isLoadingDevice });
  };

  /**
   * Records whether a facility switch is currently in progress in the create-study wizard. Merges the flag into the hook's state.
   *
   * @param isChangingFacility - True while the facility selection is being changed.
   * @returns Nothing.
   */
  const setIsChangingFacility = (isChangingFacility: any) => {
    setState({ isChangingFacility });
  };

  /**
   * Forwards a create-study wizard navigation request to the currently mounted step component. Calls handleNavigation on the step's imperative ref so the step can validate itself before the wizard moves.
   *
   * @param isNextClicked - True to move forward, false to move back.
   * @returns Nothing.
   */
  const handleNavigation = (isNextClicked: any) => {
    newStudyStepRef.current.handleNavigation(isNextClicked);
  };

  useEffect(() => {
    document.title = t`Biocare Diagnostics - New Study`;
    if (hasCardioOneFacilities(supportFacilities ?? [])) {
      navigate('/studies', { replace: true });
      return;
    }
    createInitialData();
  }, []);

  useEffect(() => {
    const bodyRightElement = document.querySelector('.app-body .custom-body__right-container');
    if (bodyRightElement) {
      bodyRightElement.scroll(0, 0);
    }
  }, [state.step]);

  return {
    state,
    newStudyData,
    isUsingStudyBiotresList,
    newStudyStepRef,
    isWebView,
    handleClick,
    handleClickBack,
    onClickYesButton,
    onClickNoButton,
    onClickAgreeDisclaimerModal,
    onClickCancelDisclaimerModal,
    updateStepTitleBiofluxDirect,
    setIsLoadedData,
    setIsLoading,
    setIsLoadingDivisions,
    setIsDisabledNext,
    setIsLoadingDevice,
    setIsChangingFacility,
    handleNavigation,
  };
};

export { useNewStudy };
